import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AuthShell } from "../components/AuthShell";
import { PhoneShowcase } from "../components/PhoneShowcase";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Checkbox } from "../components/ui/Checkbox";
import { Divider } from "../components/ui/Divider";
import { emailFieldError, isValidEmail } from "../lib/validation";
import { DURATION, EASE_OUT_SOFT } from "../lib/motion";
import { PHONE_LOGIN_ENABLED } from "../lib/featureFlags";

/**
 * Beyond Sure — Sign-In (email-entry) screen, v3.
 *
 * Brand position (locked):
 *   The headline is on-brand voice: "Insure smarter, live better." — confident,
 *   declarative, India-first, paired with the multilingual subline.
 *
 *   The PRIMARY action is BLACK, not green or blue — green is the identity
 *   color (logo, links, accents); black is the decisive action.
 *
 *   The T&C includes the mandatory advisory disclosure: BeyondSure provides
 *   advisory information only and is not a licensed insurance broker.
 *
 *   The phone-only PhonePreviewCard ("Compare 6 insurers in ~2 min") gives
 *   mobile users a reason to commit their email BEFORE typing it. It renders
 *   only ≤640px; tablet+desktop intentionally do not show it.
 *
 * Submit (commit 8cf11ef): POSTs /api/otp/send with {email}. Server-side
 * rate limits (5/IP/10min + 3/email/10min) and disposable-email gate apply.
 * On non-2xx we surface the response message in a red banner above the form;
 * on success the parent advances to OtpScreen via onSent(email).
 *
 * Google: triggers GET /api/auth/google/start?agreed_to_terms=1 — the
 * agreed_to_terms query param is mandatory (server returns 400 terms_required
 * without it, which used to look like a silent "bounce back to sign-in").
 * In Vite dev the route is proxied to Flask :5000.
 *
 * Framer Motion import:
 *   The package is imported as `framer-motion` — explicitly required by the
 *   launch brief. (The `motion/react` import path points at the same library
 *   but the brief names framer-motion by package.)
 */
type Phase = "form" | "sent";

function GoogleGlyph() {
  // Google's "G" is a logo, not a UI color — it stays branded to be
  // recognised. Rendered at 16px inside the secondary button only.
  return (
    <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6 8-11.3 8a12 12 0 1 1 0-24c3 0 5.7 1.1 7.8 3l5.7-5.7A20 20 0 1 0 24 44c11 0 20-9 20-20 0-1.3-.1-2.4-.4-3.5z" />
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16.1 19 13 24 13c3 0 5.7 1.1 7.8 3l5.7-5.7A20 20 0 0 0 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.5-5.2l-6.2-5.3a12 12 0 0 1-7.3 2.5c-5.3 0-9.7-3.4-11.3-8L6.2 33A20 20 0 0 0 24 44z" />
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3a12 12 0 0 1-4.1 5.5l6.2 5.3C40.9 35.4 44 30.1 44 24c0-1.3-.1-2.4-.4-3.5z" />
    </svg>
  );
}

function ArrowRightGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M3 8h10M9 4l4 4-4 4"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

interface SignInScreenProps {
  /** OTP was sent for this email — parent advances to the OTP screen.
   * BD1: `opts.backfill` marks a KNOWN passwordless existing account, so the
   * parent can force the set-password step right after the code verifies. */
  onSent?: (email: string, opts?: { backfill?: boolean }) => void;
  /** BD1: account exists WITH a password — parent mounts PasswordScreen. */
  onPassword?: (email: string) => void;
  /** BD1: brand-new email — parent mounts CreatePasswordScreen. */
  onCreatePassword?: (email: string) => void;
  /** Pre-fill the email field (used when navigating back from OTP). */
  initialEmail?: string;
  /** Called when the user clicks "Continue with phone" — only rendered
   *  when PHONE_LOGIN_ENABLED is true. Parent should mount PhoneSignInScreen. */
  onSwitchToPhone?: () => void;
}

export function SignInScreen({
  onSent,
  onPassword,
  onCreatePassword,
  initialEmail = "",
  onSwitchToPhone,
}: SignInScreenProps) {
  const [email, setEmail] = useState(initialEmail);
  const [touched, setTouched] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [phase, setPhase] = useState<Phase>("form");
  // Bumps on each rejected submit so the shake keyframes restart.
  const [shake, setShake] = useState(0);
  // Server-side error from /api/otp/send (rate limit, disposable email,
  // network). Distinct from `error` which is client-side validation only.
  const [submitError, setSubmitError] = useState<string | null>(null);

  const error = useMemo(
    () => (touched ? emailFieldError(email) : null),
    [email, touched],
  );
  const canSubmit = isValidEmail(email) && agreed && !loading;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setTouched(true);
    setSubmitError(null);
    if (!canSubmit) {
      setShake((n) => n + 1);
      return;
    }
    setLoading(true);
    // BD1 email-first branch: POST /api/auth/check-email decides the next
    // step — password login (exists + has_password), create-password (new
    // email), or the classic OTP path (existing passwordless → OTP verify
    // then forced backfill). If check-email is down or the parent didn't
    // wire the new callbacks, we degrade to the original OTP-only behavior
    // so sign-in NEVER breaks on the new endpoint.
    if (onPassword && onCreatePassword) {
      try {
        const cr = await fetch("/api/auth/check-email", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email }),
        });
        if (cr.status === 429) {
          const cdata = await cr.json().catch(() => ({}));
          setSubmitError(
            (cdata && (cdata.message || cdata.error)) ||
              "Too many attempts. Please wait a minute.",
          );
          setShake((n) => n + 1);
          setLoading(false);
          return;
        }
        if (cr.ok) {
          const cdata = await cr.json().catch(() => ({}));
          if (cdata && cdata.exists === true && cdata.has_password === true) {
            setLoading(false);
            onPassword(email);
            return;
          }
          if (cdata && cdata.exists === false) {
            setLoading(false);
            onCreatePassword(email);
            return;
          }
          // exists && !has_password → continue to the OTP send below with
          // the backfill flag so the parent forces set-password after verify.
          if (cdata && cdata.exists === true && cdata.has_password === false) {
            try {
              const r = await fetch("/api/otp/send", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email }),
              });
              const data = await r.json().catch(() => ({}));
              if (!r.ok) {
                setSubmitError(
                  (data && (data.message || data.error)) ||
                    "Could not send code. Please try again.",
                );
                setShake((n) => n + 1);
                setLoading(false);
                return;
              }
              setLoading(false);
              if (onSent) onSent(email, { backfill: true });
              else setPhase("sent");
              return;
            } catch {
              setSubmitError(
                "Network error. Please check your connection and try again.",
              );
              setShake((n) => n + 1);
              setLoading(false);
              return;
            }
          }
        }
        // Non-OK (500 etc) or unexpected shape → graceful OTP fallback below.
      } catch {
        /* check-email network blip → graceful OTP fallback below */
      }
    }
    // Classic path: POST /api/otp/send — server rate-limits (5/IP/10min +
    // 3/email/10min) and blocks disposable-email domains. On non-OK we
    // surface the message above the form; on OK we advance via onSent
    // (preferred) or the local "sent" phase fallback (for isolated demos).
    try {
      const r = await fetch("/api/otp/send", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        const msg =
          (data && (data.message || data.error)) ||
          "Could not send code. Please try again.";
        setSubmitError(msg);
        setShake((n) => n + 1);
        setLoading(false);
        return;
      }
      setLoading(false);
      if (onSent) {
        onSent(email);
      } else {
        setPhase("sent");
      }
    } catch {
      setSubmitError(
        "Network error. Please check your connection and try again.",
      );
      setShake((n) => n + 1);
      setLoading(false);
    }
  }

  function handleGoogle() {
    // T&C must be accepted — same gate as the email Continue button. The
    // server independently refuses /api/auth/google/start without
    // ?agreed_to_terms=1 (returns 400 terms_required), so a client-side
    // bypass cannot create an account. Without this query param the
    // request 400s and the user appears to be bounced back to sign-in.
    if (!agreed) {
      setTouched(true);
      setShake((n) => n + 1);
      return;
    }
    // The live Flask app owns this route. In Vite dev it 404s — expected.
    window.location.assign("/api/auth/google/start?agreed_to_terms=1");
  }

  return (
    <AuthShell
      eyebrow="Sign in · Free 7-day trial"
      headline="Insure smarter, live better."
      subhead="BeyondSure is India's most intelligent AI insurance advisor — in English, Hindi, and 10+ Indian languages."
      showcase={<PhoneShowcase />}
    >
      <AnimatePresence mode="wait" initial={false}>
        {phase === "form" ? (
          <motion.form
            key="form"
            onSubmit={handleSubmit}
            noValidate
            animate={{ x: shake ? [-6, 6, -4, 4, 0] : 0 }}
            transition={{ duration: DURATION.base, ease: EASE_OUT_SOFT }}
            initial={{ opacity: 0, y: 6 }}
            exit={{ opacity: 0, y: -6 }}
            className="bsure-ui flex flex-col gap-5"
          >
            {submitError ? (
              <div
                role="alert"
                aria-live="polite"
                className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-[13px] leading-snug text-red-900"
              >
                {submitError}
              </div>
            ) : null}
            <Input
              label="Email"
              type="email"
              inputMode="email"
              autoComplete="email"
              autoFocus
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={() => setTouched(true)}
              error={error}
              helper="Continue with your email — we'll take you to the right sign-in step."
            />

            <Checkbox
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              label={
                <>
                  I agree to the{" "}
                  <a
                    href="#terms"
                    className="text-[var(--color-brand)] underline underline-offset-2 decoration-[var(--color-brand)] hover:text-[var(--color-brand-deep)] transition-colors"
                  >
                    Terms of Service
                  </a>{" "}
                  and{" "}
                  <a
                    href="#privacy"
                    className="text-[var(--color-brand)] underline underline-offset-2 decoration-[var(--color-brand)] hover:text-[var(--color-brand-deep)] transition-colors"
                  >
                    Privacy Policy
                  </a>
                  . I understand BeyondSure provides advisory information only
                  and is not a licensed insurance broker.
                </>
              }
            />

            <Button
              type="submit"
              variant="primary"
              fullWidth
              loading={loading}
              disabled={!isValidEmail(email) || !agreed}
              trailing={<ArrowRightGlyph />}
            >
              Continue
            </Button>

            <Divider>or</Divider>

            <Button
              variant="secondary"
              fullWidth
              onClick={handleGoogle}
              disabled={!agreed}
              leading={<GoogleGlyph />}
            >
              Continue with Google
            </Button>

            {/*
              Phone OTP entry — hidden by default. PHONE_LOGIN_ENABLED in
              lib/featureFlags.ts flips this on when the SMS provider key is
              in prod env. The full phone flow (PhoneSignInScreen +
              PhoneOtpScreen) and the LoginRoute state-machine branches
              already exist behind the flag, so this is a true one-line
              activation.
            */}
            {PHONE_LOGIN_ENABLED && onSwitchToPhone ? (
              <Button
                variant="secondary"
                fullWidth
                onClick={() => {
                  if (!agreed) {
                    setTouched(true);
                    setShake((n) => n + 1);
                    return;
                  }
                  onSwitchToPhone();
                }}
                disabled={!agreed}
              >
                Continue with phone
              </Button>
            ) : null}

            <p className="mt-1 text-[12.5px] leading-[1.5] text-[var(--color-ink-40)]">
              New here? An account is created automatically the first time you
              sign in.
            </p>
          </motion.form>
        ) : (
          <motion.div
            key="sent"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: DURATION.base, ease: EASE_OUT_SOFT }}
            className="bsure-ui flex flex-col gap-5"
            role="status"
            aria-live="polite"
          >
            <div className="flex items-center gap-3">
              <span
                aria-hidden="true"
                className="inline-flex h-9 w-9 items-center justify-center rounded-full"
                style={{
                  backgroundColor: "var(--color-brand)",
                  color: "var(--color-paper)",
                }}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M3 8.5l3 3 7-7"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <div>
                <div className="bsure-display text-[1.5rem] leading-[1.1]">
                  Code on its way
                </div>
                <div className="text-[13.5px] text-[var(--color-ink-60)]">
                  Sent to <span className="text-[var(--color-ink)]">{email}</span>
                </div>
              </div>
            </div>

            <p className="text-[14px] leading-[1.55] text-[var(--color-ink-60)]">
              The OTP entry screen lands in the next session inside this same
              AuthShell — guaranteed visual twin.
            </p>

            <Button
              variant="secondary"
              fullWidth
              onClick={() => {
                setPhase("form");
                setLoading(false);
              }}
            >
              Use a different email
            </Button>
          </motion.div>
        )}
      </AnimatePresence>
    </AuthShell>
  );
}

export default SignInScreen;
